import { Container } from 'ui/molecules/Container'
import { MENU_NAMES } from 'texts/menuNames'
import { isOperatorSelector, userDataSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { Breadcrumb } from 'ui/atoms/Breadcrumbs/types'
import styles from './styles.module.scss'
import { Navigation } from './components/Navigation'
import { ReactNode, useEffect, useState } from 'react'
import { HistoryList } from '../HistoryList'
import { TERM_CONDITIONS_TYPES } from 'constants/termsAndConditions'
import {
  ITermsAndConditionsItem,
  ITermsAndConditionsResponse,
} from 'api/terms/types'
import { useApiRequest } from 'hooks/useApiRequest'
import { useCompanyId } from 'hooks/useCompanyId'
import { termsApi } from 'api'
import { TERMS_WITHOUTH_HISTORY } from './constants'
import { ApprovalInfoBadges } from 'ui/components/ApprovalInfoBadges'
import { ACCOUNT_STATUSES, REGISTRATION_STATUSES } from 'api/auth/constants'
import { ReviewBlock } from 'ui/components/ReviewBlock/ReviewBlock'
import { TermsSubmitButton } from '../TermsSubmitButton'
import { termsSectionsSelector } from 'redux/terms/selectors'
import { OperatorActionButtons } from 'ui/components/OperatorActionButtons'

export const RENTAL_INCLUDES_TYPE = 2

interface TermsAndConditionsProps {
  breadcrumbs?: Breadcrumb[]
  current: string
  children:
    | ReactNode
    | ((
        data?: ITermsAndConditionsItem,
        historyList?: ITermsAndConditionsItem[],
        metaData?: ITermsAndConditionsResponse['meta']
      ) => ReactNode)
  termConditionTypesId?: TERM_CONDITIONS_TYPES
  reload: VoidFunction
}

export const TermsAndConditionsContent = ({
  breadcrumbs,
  current,
  children,
  termConditionTypesId,
  reload,
}: TermsAndConditionsProps) => {
  const isOperator = useAppSelector(isOperatorSelector)
  const companyId = useCompanyId()
  const [editData, setEditData] = useState<ITermsAndConditionsItem>()
  const [historyList, setHistoryList] = useState<ITermsAndConditionsItem[]>([])
  const [metaData, setMetaData] = useState<ITermsAndConditionsResponse['meta']>(
    {}
  )
  const termsSections = useAppSelector(termsSectionsSelector)
  const { sectionsStatuses, accountStatus } = useAppSelector(userDataSelector)

  const isPendingUser =
    !isOperator &&
    sectionsStatuses?.termsConditionsStatus !== REGISTRATION_STATUSES.APPROVED

  const shouldDisplayButton =
    !isOperator &&
    [
      REGISTRATION_STATUSES.INCOMPLETE,
      REGISTRATION_STATUSES.REQUESTED,
    ].includes(sectionsStatuses?.termsConditionsStatus || '') &&
    termsSections.every((el) => el.isFilled)

  const isTermsInReview =
    (sectionsStatuses?.termsConditionsStatus ===
      REGISTRATION_STATUSES.APPROVED ||
      sectionsStatuses?.termsConditionsStatus ===
        REGISTRATION_STATUSES.REVIEW) &&
    accountStatus !== ACCOUNT_STATUSES.APPROVED &&
    !isOperator

  const getTermsListRequest = useApiRequest(
    (termConditionTypesId) =>
      termsApi.getTermConditionListRequest(companyId, termConditionTypesId),
    undefined,
    true
  )

  const getList = async () => {
    const response = await getTermsListRequest.apiRequest(termConditionTypesId)
    if (response) {
      setHistoryList(response.data.termConditions)
      setMetaData(response.data.meta)
    }
  }

  useEffect(() => {
    getList()
  }, [termConditionTypesId])

  return (
    <Container
      title={MENU_NAMES.TERMS_AND_CONDITIONS}
      breadcrumbList={breadcrumbs}
      currentPageLabel={MENU_NAMES.TERMS_AND_CONDITIONS}
      withNavigation={isOperator}
      rightBlock={shouldDisplayButton && <TermsSubmitButton />}
      actionButtons={() => (
        <OperatorActionButtons
          section="TermsAndConditions"
          companyId={String(companyId)}
        />
      )}
    >
      {isPendingUser && !isTermsInReview && (
        <ApprovalInfoBadges
          isRequested={
            sectionsStatuses?.termsConditionsStatus ===
            REGISTRATION_STATUSES.REQUESTED
          }
        />
      )}
      {isTermsInReview ? (
        <ReviewBlock
          isApproved={
            sectionsStatuses?.termsConditionsStatus ===
            REGISTRATION_STATUSES.APPROVED
          }
        />
      ) : (
        <div className={styles.container}>
          <Navigation current={current} />
          {!getTermsListRequest.loading && (
            <div className={styles.content}>
              {typeof children === 'function'
                ? children(editData, historyList, metaData)
                : children}
              {!!termConditionTypesId &&
                !TERMS_WITHOUTH_HISTORY.includes(termConditionTypesId) && (
                  <HistoryList
                    termConditionTypesId={termConditionTypesId}
                    setEditData={setEditData}
                    editDataId={editData?.id}
                    historyList={historyList}
                    getList={getList}
                    reload={reload}
                  />
                )}
            </div>
          )}
        </div>
      )}
    </Container>
  )
}
